import { Controller } from "@hotwired/stimulus"
import Swiper from "swiper"
import { Navigation, Pagination, Keyboard } from "swiper/modules"

export default class extends Controller {
  static targets = ["dialog", "title", "slidesWrapper", "pagination", "prevButton", "nextButton"]

  connect() {
    this.swiper = null
  }

  disconnect() {
    this.destroySwiper()
  }

  open(event) {
    event.preventDefault()
    event.stopPropagation()

    const payload = event.currentTarget.dataset.claimTrackDocumentsPayload
    const title = event.currentTarget.dataset.claimTrackDocumentsTitle
    const documents = this.parseDocuments(payload)
    if (!documents.length) return

    this.titleTarget.textContent = title || "Documents"
    this.renderSlides(documents)
    this.openDialog()
    this.initializeSwiper()
  }

  close(event) {
    event?.preventDefault()
    this.dialogTarget.close()
    this.resetGallery()
  }

  handleDialogClosed() {
    this.resetGallery()
  }

  parseDocuments(payload) {
    try {
      const parsed = JSON.parse(payload || "[]")
      return Array.isArray(parsed) ? parsed : []
    } catch (_error) {
      return []
    }
  }

  openDialog() {
    if (this.dialogTarget.open) return
    this.dialogTarget.showModal()
  }

  initializeSwiper() {
    this.destroySwiper()
    this.swiper = new Swiper(this.dialogTarget.querySelector(".swiper"), {
      modules: [Navigation, Pagination, Keyboard],
      slidesPerView: 1,
      spaceBetween: 16,
      keyboard: { enabled: true },
      navigation: {
        prevEl: this.prevButtonTarget,
        nextEl: this.nextButtonTarget
      },
      pagination: {
        el: this.paginationTarget,
        clickable: true
      }
    })
  }

  renderSlides(documents) {
    const slides = documents.map((doc) => {
      const filename = this.escapeHtml(doc.filename || "Document")
      const viewUrl = this.escapeHtml(doc.view_url || "#")
      const downloadUrl = this.escapeHtml(doc.download_url || "#")
      const contentType = doc.content_type || ""

      if (contentType.startsWith("image/")) {
        return `
          <div class="swiper-slide">
            <figure class="rounded-box border border-base-300 bg-base-200 p-2">
              <img src="${viewUrl}" alt="${filename}" class="mx-auto max-h-[60vh] w-auto rounded-box object-contain" loading="lazy" />
            </figure>
            <div class="mt-3 flex items-center justify-between gap-3">
              <p class="truncate text-sm text-base-content/80" title="${filename}">${filename}</p>
              <a href="${downloadUrl}" target="_blank" rel="noopener" class="btn btn-sm gap-2">
                <span class="icon-[lucide--download] size-4"></span>
                Download
              </a>
            </div>
          </div>
        `
      }

      return `
        <div class="swiper-slide">
          <div class="rounded-box border border-base-300 bg-base-200 p-8 text-center">
            <span class="icon-[lucide--file-text] mx-auto mb-4 block size-12 text-base-content/60"></span>
            <p class="mb-4 break-all font-medium">${filename}</p>
            <div class="flex flex-wrap items-center justify-center gap-2">
              <a href="${viewUrl}" target="_blank" rel="noopener" class="btn btn-sm btn-outline gap-2">
                <span class="icon-[lucide--external-link] size-4"></span>
                Open
              </a>
              <a href="${downloadUrl}" target="_blank" rel="noopener" class="btn btn-sm gap-2">
                <span class="icon-[lucide--download] size-4"></span>
                Download
              </a>
            </div>
          </div>
        </div>
      `
    }).join("")

    this.slidesWrapperTarget.innerHTML = slides
  }

  resetGallery() {
    this.destroySwiper()
    this.slidesWrapperTarget.innerHTML = ""
    this.titleTarget.textContent = "Documents"
  }

  destroySwiper() {
    if (!this.swiper) return
    this.swiper.destroy(true, true)
    this.swiper = null
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;")
      .replaceAll("'", "&#039;")
  }
}
